/**
 * Phonetic Alphabet Reference
 * IPA phonetic symbols for English pronunciation with American/British conversion
 */

export interface PhoneticSymbol {
  symbol: string;
  example: string;
  exampleWord: string;
  soundFile?: string;
  description: string;
  category: 'vowel' | 'consonant';
}

// American English IPA Phonetic Symbols
export const americanPhonetics: PhoneticSymbol[] = [
  // Vowels - Monophthongs (单元音)
  { symbol: '/æ/', example: 'cat', exampleWord: 'cat', description: '前元音，嘴张大', category: 'vowel' },
  { symbol: '/ɛ/', example: 'bed', exampleWord: 'bed', description: '前元音，嘴半开', category: 'vowel' },
  { symbol: '/ɪ/', example: 'sit', exampleWord: 'sit', description: '前元音，嘴半合', category: 'vowel' },
  { symbol: '/ɑ/', example: 'hot', exampleWord: 'hot', description: '后元音，嘴张大', category: 'vowel' },
  { symbol: '/ɔ/', example: 'dog', exampleWord: 'dog', description: '后元音，嘴半开', category: 'vowel' },
  { symbol: '/ʊ/', example: 'put', exampleWord: 'put', description: '后元音，嘴半合', category: 'vowel' },
  { symbol: '/ʌ/', example: 'cup', exampleWord: 'cup', description: '中元音', category: 'vowel' },
  { symbol: '/ə/', example: 'about', exampleWord: 'about', description: '中元音，弱读音', category: 'vowel' },

  // Vowels - Diphthongs (双元音)
  { symbol: '/eɪ/', example: 'face', exampleWord: 'face', description: '双元音 e→ɪ', category: 'vowel' },
  { symbol: '/aɪ/', example: 'time', exampleWord: 'time', description: '双元音 a→ɪ', category: 'vowel' },
  { symbol: '/ɔɪ/', example: 'boy', exampleWord: 'boy', description: '双元音 ɔ→ɪ', category: 'vowel' },
  { symbol: '/aʊ/', example: 'house', exampleWord: 'house', description: '双元音 a→ʊ', category: 'vowel' },
  { symbol: '/oʊ/', example: 'go', exampleWord: 'go', description: '双元音 o→ʊ', category: 'vowel' },

  // Consonants - Stops (爆破音)
  { symbol: '/p/', example: 'pen', exampleWord: 'pen', description: '清辅音，双唇爆破', category: 'consonant' },
  { symbol: '/b/', example: 'bad', exampleWord: 'bad', description: '浊辅音，双唇爆破', category: 'consonant' },
  { symbol: '/t/', example: 'tea', exampleWord: 'tea', description: '清辅音，舌尖爆破', category: 'consonant' },
  { symbol: '/d/', example: 'dog', exampleWord: 'dog', description: '浊辅音，舌尖爆破', category: 'consonant' },
  { symbol: '/k/', example: 'cat', exampleWord: 'cat', description: '清辅音，舌根爆破', category: 'consonant' },
  { symbol: '/g/', example: 'go', exampleWord: 'go', description: '浊辅音，舌根爆破', category: 'consonant' },

  // Consonants - Fricatives (摩擦音)
  { symbol: '/f/', example: 'five', exampleWord: 'five', description: '清辅音，唇齿摩擦', category: 'consonant' },
  { symbol: '/v/', example: 'very', exampleWord: 'very', description: '浊辅音，唇齿摩擦', category: 'consonant' },
  { symbol: '/s/', example: 'see', exampleWord: 'see', description: '清辅音，舌尖摩擦', category: 'consonant' },
  { symbol: '/z/', example: 'zoo', exampleWord: 'zoo', description: '浊辅音，舌尖摩擦', category: 'consonant' },
  { symbol: '/ʃ/', example: 'she', exampleWord: 'she', description: '清辅音，舌面摩擦', category: 'consonant' },
  { symbol: '/ʒ/', example: 'vision', exampleWord: 'vision', description: '浊辅音，舌面摩擦', category: 'consonant' },
  { symbol: '/θ/', example: 'think', exampleWord: 'think', description: '清辅音，舌尖齿间', category: 'consonant' },
  { symbol: '/ð/', example: 'this', exampleWord: 'this', description: '浊辅音，舌尖齿间', category: 'consonant' },
  { symbol: '/h/', example: 'hat', exampleWord: 'hat', description: '清辅音，声门摩擦', category: 'consonant' },
  { symbol: '/r/', example: 'red', exampleWord: 'red', description: '浊辅音，舌尖卷起', category: 'consonant' },

  // Consonants - Nasals (鼻音)
  { symbol: '/m/', example: 'man', exampleWord: 'man', description: '浊辅音，双唇鼻音', category: 'consonant' },
  { symbol: '/n/', example: 'no', exampleWord: 'no', description: '浊辅音，舌尖鼻音', category: 'consonant' },
  { symbol: '/ŋ/', example: 'sing', exampleWord: 'sing', description: '浊辅音，舌根鼻音', category: 'consonant' },

  // Consonants - Liquids (流音)
  { symbol: '/l/', example: 'leg', exampleWord: 'leg', description: '浊辅音，舌尖侧流', category: 'consonant' },

  // Consonants - Glides (滑音)
  { symbol: '/w/', example: 'we', exampleWord: 'we', description: '浊辅音，双唇滑音', category: 'consonant' },
  { symbol: '/j/', example: 'yes', exampleWord: 'yes', description: '浊辅音，舌面滑音', category: 'consonant' },
];

/**
 * American to British Phonetic Conversion Rules
 * Extended rules for converting American IPA to British IPA
 *
 * IMPORTANT: Uses Unicode IPA characters (not ASCII equivalents)
 */
export const americanToBritishRules: Record<string, string> = {
  // Vowel conversions (元音转换) - Unicode IPA symbols
  '/ɑ/': '/ɒ/',     // hot, stop, top (美式 /ɑ/ → 英式 /ɒ/)
  '/ɔ/': '/ɒ/',     // dog, lot (美式 /ɔ/ → 英式 /ɒ/)
  '/oʊ/': '/əʊ/',   // go, no, so (美式 /oʊ/ → 英式 /əʊ/)

  // Unicode variants that appear in data
  '/ɡoʊ/': '/ɡəʊ/', // go with script g (U+0261)
  '/ɡər/': '/ɡə/',  // teacher with script g

  // R-dropping rules (r 音省略规则)
  // British English drops 'r' after vowels at end of words or before consonants
  '/ər/': '/ə/',    // teacher, paper (美式 /ər/ → 英式 /ə/)
  '/ɪr/': '/ɪ/',    // near, here (美式 /ɪr/ → 英式 /ɪ/)
  '/ɛr/': '/ɛ/',    // there, where (美式 /ɛr/ → 英式 /ɛ/)
  '/ʊr/': '/ʊ/',    // poor, tour (美式 /ʊr/ → 英式 /ʊ/)
  '/ɑr/': '/ɑ/',    // car, far (美式 /ɑr/ → 英式 /ɑ/)
  '/ɔr/': '/ɔ/',    // more, for (美式 /ɔr/ → 英式 /ɔ/)
  '/aɪər/': '/aɪə/', // fire, tire (美式 /aɪər/ → 英式 /aɪə/)
  '/aʊər/': '/aʊə/', // flower, power (美式 /aʊər/ → 英式 /aʊə/)

  // Specific common patterns found in phrase data
  'oʊ': 'əʊ',       // Without slashes (appears in: /ɡoʊ/)
  'ɑr': 'ɑ',        // car pattern
  'ər': 'ə',        // teacher pattern

  // Special consonant cases (特殊辅音情况)
  '/r/': '',        // Standalone r after vowels should be removed
};

/**
 * British to American Phonetic Conversion Rules
 * Reverse conversion for completeness
 */
export const britishToAmericanRules: Record<string, string> = {
  '/ɒ/': '/ɑ/',     // Reverse of vowel conversion
  '/əʊ/': '/oʊ/',   // Reverse of diphthong conversion
};

// Legacy compatibility - keep old function name
export const britishPhoneticDifferences = americanToBritishRules;

// Helper functions
export function getPhoneticBySymbol(symbol: string): PhoneticSymbol | undefined {
  return americanPhonetics.find(p => p.symbol === symbol);
}

export function getVowels(): PhoneticSymbol[] {
  return americanPhonetics.filter(p => p.category === 'vowel');
}

export function getConsonants(): PhoneticSymbol[] {
  return americanPhonetics.filter(p => p.category === 'consonant');
}

/**
 * Convert American phonetic notation to British
 * @param americanPhonetic - American IPA notation (e.g., "/ˈtɑɪɡər/")
 * @returns British IPA notation (e.g., "/ˈtaɪɡə/")
 */
export function convertToBritishPhonetic(americanPhonetic: string): string {
  let british = americanPhonetic;

  // Apply conversion rules in order
  for (const [am, br] of Object.entries(americanToBritishRules)) {
    if (br === '') {
      // Remove the symbol (e.g., silent r)
      british = british.replace(new RegExp(am.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), '');
    } else {
      british = british.replace(new RegExp(am.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), br);
    }
  }

  return british;
}

/**
 * Convert British phonetic notation to American
 * @param britishPhonetic - British IPA notation
 * @returns American IPA notation
 */
export function convertToAmericanPhonetic(britishPhonetic: string): string {
  let american = britishPhonetic;

  for (const [br, am] of Object.entries(britishToAmericanRules)) {
    american = american.replace(new RegExp(br.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), am);
  }

  return american;
}

/**
 * Convert generic phonetic notation to both American and British variants
 * @param genericPhonetic - Generic IPA notation (usually closer to American)
 * @returns Object with both American and British variants
 */
export function convertToBothPhonetics(genericPhonetic: string): { american: string; british: string } {
  // Assume generic phonetic is American-style
  const american = genericPhonetic;
  const british = convertToBritishPhonetic(genericPhonetic);

  return { american, british };
}

/**
 * Get phonetic with fallback to conversion
 * @param phonetic - Original phonetic notation
 * @param phoneticUS - American specific (if available)
 * @param phoneticUK - British specific (if available)
 * @param preferredStyle - User's preferred pronunciation style
 */
export function getPhoneticWithStyle(
  phonetic?: string,
  phoneticUS?: string,
  phoneticUK?: string,
  preferredStyle: 'american' | 'british' = 'american'
): string {
  // If specific variants exist, use them directly
  if (preferredStyle === 'american' && phoneticUS) return phoneticUS;
  if (preferredStyle === 'british' && phoneticUK) return phoneticUK;

  // If only generic phonetic exists, convert it
  if (phonetic) {
    const converted = convertToBothPhonetics(phonetic);
    return preferredStyle === 'american' ? converted.american : converted.british;
  }

  // No phonetic available
  return '';
}

export function getPhoneticCategoryName(category: 'vowel' | 'consonant'): string {
  return category === 'vowel' ? '元音' : '辅音';
}